import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ArrowBendDoubleUpRight } from "@phosphor-icons/react/ArrowBendDoubleUpRight";
import { ArrowsClockwise } from "@phosphor-icons/react/ArrowsClockwise";
import { Buildings } from "@phosphor-icons/react/Buildings";
import { CloudArrowUp } from "@phosphor-icons/react/CloudArrowUp";
import { FilePlus } from "@phosphor-icons/react/FilePlus";
import { Globe } from "@phosphor-icons/react/Globe";
import { Handshake } from "@phosphor-icons/react/Handshake";
import { Layout } from "@phosphor-icons/react/Layout";
import { Lightning } from "@phosphor-icons/react/Lightning";
import { Link } from "@phosphor-icons/react/Link";
import { ListMagnifyingGlass } from "@phosphor-icons/react/ListMagnifyingGlass";
import { MapPin } from "@phosphor-icons/react/MapPin";
import { MapTrifold } from "@phosphor-icons/react/MapTrifold";
import { PaintBrush } from "@phosphor-icons/react/PaintBrush";
import { PiggyBank } from "@phosphor-icons/react/PiggyBank";
import { Quotes } from "@phosphor-icons/react/Quotes";
import { Tag } from "@phosphor-icons/react/Tag";
import { UsersThree } from "@phosphor-icons/react/UsersThree";
import { mapPalettes } from "mangomap-ui/src/components/map-field";

import type { BenefitsContent } from "~landing/components/home/benefits";
import type { FaqsContent } from "~landing/components/home/faqs";
import type { FeatureHighlightsContent } from "~landing/components/home/feature-highlights";
import type { HeroContent } from "~landing/components/home/hero";
import type { HowItWorksContent } from "~landing/components/home/how-it-works";
import type { PricingTeaserContent } from "~landing/components/home/pricing-teaser";
import type { StartBuildingContent } from "~landing/components/home/start-building";
import type { TestimonialsContent } from "~landing/components/home/testimonials";
import type { UseCasesContent } from "~landing/components/home/use-cases";

// Government solutions copy, rendered through the shared home section template.

const hero: HeroContent = {
	eyebrow: "LocGov",
	icon: <UsersThree />,
	heading: "Publish your county's maps. Pay a price that doesn't move.",
	subHeading:
		"Mango is the publishing layer for finished county GIS work. Put your parcel, zoning, and E911 maps online, keep them current, and pay the same fixed price year after year.",
	ctaButton: "Start Your Free Trial",
	keyObjection:
		"No credit card required to start. Annual billing and bank transfer when you are ready to buy.",
	socialProof:
		"“Five years in and it just keeps working. Residents find their own parcel, and I get my time back.” Trusted by counties and townships like Columbus, Plumas, and Stowe.",
	socialProofLabel: "Used by teams like:",
	image: "/app-assets/images/hero-map.jpg",
	// Hero MapField background — tune these to restyle the government hero.
	// variant: contour | voronoi | hex | delaunay | parcels
	// palette: mapPalettes.{ bathymetric | topoPaper | indigoInk | forest | slate | onDark }
	mapField: {
		seed: "mango-government",
		variant: "parcels",
		palette: mapPalettes.slate,
		strokeWidth: 0.5,
		parcelMinSize: 0.5,
		parcelMaxDepth: 10,
		animate: true,
		duration: 5,
		opacity: 0.12,
	},
	customers: [
		{
			customer: "Sensar",
			src: "/app-assets/images/customer-sensar.png",
		},
		{
			customer: "GIS4AG",
			src: "/app-assets/svg/customer-gis4ag.svg",
		},
		{
			customer: "Geotech Info",
			src: "/app-assets/images/customer-geotech-info.png",
		},
		{
			customer: "Enerko",
			src: "/app-assets/svg/customer-enerko.svg",
		},
		{
			customer: "Swift Aerial Surveys",
			src: "/app-assets/svg/customer-swift.svg",
		},
		{
			customer: "GBI Experts",
			src: "/app-assets/svg/customer-gbi.svg",
		},
		{
			customer: "Plumas County",
			src: "/app-assets/images/customer-plumas.png",
		},
	],
};

const benefits: BenefitsContent = {
	heading: "Fixed pricing you can budget for and defend at renewal",
	subHeading:
		"Annual and multi-year plans, bank transfer, every cost known in advance. Priced so the spend is easy to predict and easy to justify.",
	cards: [
		{
			eyebrow: "Save",
			icon: <PiggyBank />,
			title: "Fixed price, no surprises at renewal",
			description:
				"Fixed annual and multi-year plans with no hidden extras. Add premium seats only if you need them, always at a fixed, known fee. The same number every year, easy to budget and easy to take to the committee.",
			image: "/app-assets/images/ink-pricing-fixed-dashboard.png",
		},
		{
			eyebrow: "Speed",
			icon: <Lightning />,
			title: "Up and running in hours",
			description:
				"Bring your existing maps and have them live the same day. No platform to roll out, no server for IT to stand up or patch.",
			image: "/app-assets/images/ink-import-to-live-speed.png",
		},
		{
			eyebrow: "Sync",
			icon: <ArrowsClockwise />,
			title: "Your existing data, unchanged",
			description:
				"Bring the Shapefiles, GeoJSON, and GeoTIFF you already have. Same schema, same coordinate system. Nothing to rebuild.",
			image: "/app-assets/images/ink-import-file-format-single.png",
		},
		{
			eyebrow: "Team",
			icon: <UsersThree />,
			title: "One account manager, start to finish",
			description:
				"From trial through procurement and renewal, you work with one account manager who knows your county's setup. Not a fresh stranger every time.",
			image: "/app-assets/images/ink-support-account-manager.png",
		},
		{
			eyebrow: "Sync",
			icon: <ArrowsClockwise />,
			title: "Update once, current everywhere",
			description:
				"When parcel lines move or zoning changes, update the data once. Every published map and embed reflects it. Residents never land on last year's version.",
			image: "/app-assets/images/ink-map-update-refresh.png",
		},
	],
};

const useCases: UseCasesContent = {
	heading: "Local governments already doing this with Mango",
	subHeading:
		"From county portals to small-town web maps — real teams, real maps.",
	cards: [
		{
			id: "use-case-county-portal",
			eyebrow: "Local government",
			icon: <Buildings />,
			title: "County and city map portals",
			description:
				"Columbus County, North Carolina replaced an antiquated server-based GIS with a Mango portal that now draws more than 17,000 views every month.",
			image: "/app-assets/images/articles/columbus-county-map-portal/hero.jpg",
			link: "/case-studies/columbus-county-map-portal",
		},
		{
			id: "use-case-cost-savings",
			eyebrow: "Cost savings",
			icon: <PiggyBank />,
			title: "Cut GIS costs without cutting features",
			description:
				"A Tennessee 911 office in Henry County replaced a $30,000 Esri quote with a $2,000 blend of QGIS, Fulcrum, and Mango — without giving anything up.",
			image:
				"/app-assets/images/articles/henry-county-reduced-gis-costs-10x/hero.jpg",
			link: "/case-studies/henry-county-reduced-gis-costs-10x",
		},
		{
			id: "use-case-replace-platform",
			eyebrow: "Local government",
			icon: <MapTrifold />,
			title: "Replace an outdated public map",
			description:
				"Plumas County's GIS division swapped an out-of-date public mapping platform for an intuitive, cost-effective Mango portal.",
			image: "/app-assets/images/articles/plumas-county-online-gis/hero.png",
			link: "/case-studies/plumas-county-online-gis",
		},
		{
			id: "use-case-small-town",
			eyebrow: "Small towns",
			icon: <MapPin />,
			title: "Enterprise maps on a small-town budget",
			description:
				"The Town of Stettler, Alberta delivered first-class public web maps on a budget that ruled out enterprise GIS software.",
			image:
				"/app-assets/images/articles/town-of-stettler-web-mapping/hero.png",
			link: "/case-studies/town-of-stettler-web-mapping",
		},
	],
};

const testimonials: TestimonialsContent = {
	eyebrow: "Success stories",
	icon: <Quotes />,
	reviews: [
		{
			id: "REV-1",
			author: "Graham Scott",
			title: "GIS Coordinator, Town of Stettler",
			logo: "/app-assets/images/customer-stettler.svg",
			comment:
				"I would highly recommend Mango to anyone in local government, as it can become such an asset to your staff and community.",
		},
		{
			id: "REV-2",
			author: "Alan James",
			title: "Director of Management Information Systems, Columbus County",
			logo: "/app-assets/images/customer-columbus.png",
			comment:
				"What immediately impressed me was how quickly I was able to upload data and get a useful map published. Other products I tried required more time in the learning curve than I could afford.",
		},
		{
			id: "REV-3",
			author: "Becky Osborn",
			title: "GIS Coordinator, Plumas County, CA",
			logo: "/app-assets/images/customer-plumas.png",
			comment:
				"Since implementing Mango into our GIS department’s website, we went from ‘basic and boring’… to ‘WOW!’",
		},
		{
			id: "REV-4",
			author: "Emily Selphe",
			title: "Charlevoix County, MI",
			logo: "/app-assets/images/customer-charlevoix.png",
			comment:
				"A great out-of-the-box product—professional results with minimal time and money spent.",
		},
	],
};

const howItWorks: HowItWorksContent = {
	steps: [
		{
			id: "import",
			eyebrow: "Import",
			icon: <CloudArrowUp />,
			image: "/app-assets/images/ink-import-file-formats.png",
			imageAlt: "Importing county GIS files into MangoMap",
			title: "Add Your Data",
			description:
				"Drop in the Shapefiles, GeoJSON, GeoTIFF, and KML you already have. Your schema and coordinate system come with them.",
		},
		{
			id: "design",
			eyebrow: "Design",
			icon: <MapTrifold />,
			image: "/app-assets/images/ink-style-editor-branded.png",
			imageAlt: "Styling a county map in MangoMap",
			title: "Build Your Map",
			description:
				"Style it with popups, a legend, and your county's logo and colours. Point and click, no code.",
		},
		{
			id: "distribute",
			eyebrow: "Distribute",
			icon: <ArrowBendDoubleUpRight />,
			image: "/app-assets/images/ink-share-embed-website.png",
			imageAlt: "Sharing a trusted county map link",
			title: "Share the Link",
			description:
				"Publish to a link or embed it on your county site. People open it with no install and no account.",
		},
		{
			id: "update",
			eyebrow: "Update",
			icon: <ArrowsClockwise />,
			image: "/app-assets/images/ink-map-update-before-after.png",
			imageAlt: "Updated county data reflected in a live map",
			title: "Keep It Current",
			description:
				"Add new data as it comes in. Every published map updates right away, so nobody sees a stale version.",
		},
		{
			id: "organize",
			eyebrow: "Organize",
			icon: <Layout />,
			image: "/app-assets/images/ink-maps-portal-team-pricing.png",
			imageAlt: "Managing county projects and access in MangoMap",
			title: "Stay Organised",
			description:
				"Track every map and portal in one place. Add team members and set who can see each map.",
		},
		{
			id: "handoff",
			eyebrow: "Handoff",
			icon: <Handshake />,
			image: "/app-assets/images/ink-map-branded-portal-page.png",
			imageAlt: "Residents exploring a published county portal",
			title: "Hand It Off",
			description:
				"Send the link and step back. The map answers the questions that used to come to your desk.",
		},
	],
};

const faqs: FaqsContent = {
	faqContent: {
		eyebrow: "FAQ",
		heading: "Need more answers?",
		subHeading: (
			<Typography color="text.secondary">
				Check our&nbsp;
				<Box
					component="a"
					href="https://help.mangomap.com/hc/en-us/sections/360007615811-Frequently-Asked-Questions"
					sx={{ color: "inherit", textDecoration: "underline" }}>
					complete FAQ
				</Box>
				&nbsp;in the help center, or&nbsp;
				<Box
					component="a"
					href="mailto:sales@mangomap.com"
					sx={{ color: "inherit", textDecoration: "underline" }}>
					contact us
				</Box>
				&nbsp;directly.
			</Typography>
		),
	},
	faqs: [
		{
			id: "FAQ-1",
			question: "How does the pricing work?",
			answer:
				"Fixed annual and multi-year plans. The plan price is set, and any premium add-on seats are a fixed fee on top, so nothing is metered or unpredictable. The same number every year makes it easy to budget.",
		},
		{
			id: "FAQ-2",
			question: "Do I need to self-host?",
			answer:
				"No. It is fully hosted. Nothing for county IT to stand up or maintain.",
		},
		{
			id: "FAQ-3",
			question: "Can I brand my maps?",
			answer:
				"Yes. Add your own logo, styling, and domain to every map and portal.",
		},
		{
			id: "FAQ-4",
			question: "Will residents like it?",
			answer:
				"Residents open a link and explore the map on any device. No login, no GIS software, no manual.",
		},
		{
			id: "FAQ-5",
			question: "Does it fit procurement?",
			answer:
				"We know the procurement lifecycle and can work with you through it. Bank transfer and annual billing keep it committee-friendly.",
		},
		{
			id: "FAQ-6",
			question: "Is it fast to set up?",
			answer: "From upload to a live map in hours, not weeks.",
		},
		{
			id: "FAQ-7",
			question: "Is this a replacement for our full GIS suite?",
			answer:
				"No. Your analysis stays where it is. Mango is the publishing layer that gets finished maps online for the people who do not have GIS software.",
		},
		{
			id: "FAQ-8",
			question: "What if I need help?",
			answer:
				"You get a dedicated account manager who knows your account and is there to help you get set up and unstuck.",
		},
	],
};

const startBuilding: StartBuildingContent = {
	heading: "Dependable county mapping, at a price you can plan around",
	subHeading:
		"Bring your existing maps and publish them to your county site. Fixed annual pricing, bank transfer, and an account manager who knows your account. Nothing to host, nothing to patch.",
	ctaButton: "Start Your Free Trial",
	ctaFooter:
		"Tired of bracing for the next renewal and patching a server that should just run? Start with a free trial, no credit card, and see how it fits before you take it to the committee.",
	image: "/app-assets/images/cta-map.jpg",
};

const featureHighlights: FeatureHighlightsContent = {
	heading: "Everything between the data and the link.",
	features: [
		{
			eyebrow: "Import",
			title: "Vector Data Support",
			description:
				"Shapefile, MapInfo Tab, KML, CSV, FileGeodatabase, and GeoJSON. Upload what you already have.",
			icon: (
				<FilePlus
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Filter",
			title: "Ask Your Data Anything",
			description:
				"Build powerful filters on any field - without writing a line of SQL.",
			icon: (
				<ListMagnifyingGlass
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Style",
			title: "Advanced Symbology Builder",
			description:
				"Thematic, category, heatmap, and custom graphic styles. Built once, reused across every map.",
			icon: (
				<PaintBrush
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Share",
			title: "Share with Anyone",
			description:
				"Public links for clients, login-required links for internal audiences. You decide who sees what.",
			icon: (
				<Link
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Brand",
			title: "Fully Branded Maps",
			description:
				"Custom domain, logo, and colours, so every map you publish looks like it came from you, not us.",
			icon: (
				<Tag
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
		{
			eyebrow: "Analyze",
			title: "Spatial Analysis",
			description:
				"Proximity, buffer, and intersection. Run the analyses your clients need without opening desktop GIS software.",
			icon: (
				<Globe
					color="var(--mui-palette-secondary-main)"
					size={22}
					weight="duotone"
				/>
			),
		},
	],
};

const pricingTeaser: PricingTeaserContent = {
	heading: "The publishing job shouldn't cost enterprise money.",
	subHeading: "Fixed price plans. No surprises.",
	ctaLabel: "See pricing",
};

export const governmentContent = {
	hero,
	benefits,
	useCases,
	testimonials,
	howItWorks,
	faqs,
	featureHighlights,
	pricingTeaser,
	startBuilding,
};
